import { decodeJwtPayload } from 'Utils/decodeJwtPayload';

export enum USER_TYPE {
  ShikumUser = '35',
  Representative = '54',
}

export const representativesAllowedUrls = ['/lawyers-lobby', '/documents', '/universalRequest', '/applies', '/recognitionStatus'];

export const LAWYERS_LOBBY_PATH = '/lawyers-lobby';
export const CONTACT_DETAILS_PATH = '/contact-details';

export type RepresentativeRedirect = typeof LAWYERS_LOBBY_PATH | typeof CONTACT_DETAILS_PATH | '/' | null;

export function needsContactDetailsOnboarding(isNewUserClaim: boolean, isFirstTimeSession: string | null): boolean {
  return isNewUserClaim && isFirstTimeSession !== 'true';
}

function isLawyerOrShikumUser(decodedToken: any, userType: USER_TYPE): boolean {
  return decodedToken?.userRanks?.findIndex((r: string) => r === (userType === USER_TYPE.Representative ? USER_TYPE.Representative : USER_TYPE.ShikumUser)) > -1;
}

export function isRepresentativesAllowedPath(pathname: string): boolean {
  const path = pathname.toLowerCase();
  return representativesAllowedUrls.some((url) => path.includes(url.toLowerCase()));
}

export function isLawyerLobbyPath(pathname: string): boolean {
  return pathname.toLowerCase().includes(LAWYERS_LOBBY_PATH);
}

export function isContactDetailsPath(pathname: string): boolean {
  return pathname.toLowerCase().includes(CONTACT_DETAILS_PATH);
}

export function readLawyerAreaFromSession(): boolean {
  return sessionStorage.getItem('isLawyerArea') === 'true';
}

export function tokenIncludesUserRanks(token: string | null): boolean {
  if (!token) return false;
  const decodedToken = decodeJwtPayload(token);
  return Array.isArray(decodedToken?.userRanks);
}

export function checkRepresentativeRestrictions(token: string | null): boolean {
  if (!token) return false;
  const decodedToken = decodeJwtPayload(token);
  return decodedToken && isLawyerOrShikumUser(decodedToken, USER_TYPE.Representative) && !isLawyerOrShikumUser(decodedToken, USER_TYPE.ShikumUser);
}

export function isLawyerAndShikumUser(token: string | null): boolean {
  if (!token) return false;
  const decodedToken = decodeJwtPayload(token);
  return !!decodedToken && isLawyerOrShikumUser(decodedToken, USER_TYPE.Representative) && isLawyerOrShikumUser(decodedToken, USER_TYPE.ShikumUser);
}

export function shouldUseLawyerLobby(representativeOnly: boolean, lawyerAndShikum: boolean, lawyerArea: boolean): boolean {
  return representativeOnly || (lawyerAndShikum && lawyerArea);
}

export interface ILawyerZoneFlags {
  representativeOnly: boolean;
  lawyerAndShikum: boolean;
  isLawyerArea: boolean;
}

export function lawyerZoneFromToken(token: string | null, isLawyerArea: boolean): boolean {
  return shouldUseLawyerLobby(checkRepresentativeRestrictions(token), isLawyerAndShikumUser(token), isLawyerArea);
}

export function lawyerZoneFromServerUser(isRepresentativeAuthenticatedUser: boolean, isShikumAuthenticatedUser: boolean, isLawyerArea: boolean): boolean {
  const representativeOnly = isRepresentativeAuthenticatedUser && !isShikumAuthenticatedUser;
  const lawyerAndShikum = isRepresentativeAuthenticatedUser && isShikumAuthenticatedUser;
  return shouldUseLawyerLobby(representativeOnly, lawyerAndShikum, isLawyerArea);
}

export function resolveRepresentativeRedirect(inLawyerZone: boolean, pathname: string, needsContactDetails = false): RepresentativeRedirect {
  if (needsContactDetails && !isContactDetailsPath(pathname)) {
    return CONTACT_DETAILS_PATH;
  }

  if (inLawyerZone) {
    if (isContactDetailsPath(pathname)) {
      return null;
    }
    if (!isRepresentativesAllowedPath(pathname)) {
      return LAWYERS_LOBBY_PATH;
    }
    return null;
  }

  if (isLawyerLobbyPath(pathname)) {
    return '/';
  }

  return null;
}
